import { isAdminRoleName } from './roleOptions';

// Central permission helper (first of its kind in this panel — every page
// previously rendered its own buttons unconditionally). Introduced with the
// Auditor role (Final Review, 2026-08-23): an Auditor sees everything but
// mutates nothing; their only write anywhere is signing an Auditor Approval
// on the audit log. The server enforces the same contract
// (middleware/auditorReadOnly), so this layer is UX, not security.

export const isAuditorRoleName = (name?: string): boolean =>
  String(name || '').trim().toLowerCase() === 'auditor';

// The signed-in user's role name, tolerant of both storage shapes
// (role as a string on new APIs, { name } on older ones) and both
// localStorage layouts ({...user} vs { user: {...} }).
export const currentUserRoleName = (): string => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    const role = stored.role ?? stored.user?.role;
    return typeof role === 'string' ? role : role?.name || '';
  } catch {
    return '';
  }
};

export const currentUserIsAuditor = (): boolean =>
  isAuditorRoleName(currentUserRoleName());

// Gate for every mutation control (save/add/edit/delete buttons, transaction
// actions). Everyone except Auditors may mutate; the Auditor's one permitted
// write (the sign-off) has its own dedicated control in the audit detail.
export const canMutate = (): boolean => !currentUserIsAuditor();

// AUD-06 (locked 2026-08-24): the audit log's "Show Admin Activity" toggle is
// for Auditors and Administrators only. Everyone else must neither see the
// control nor be able to pull admin rows into the grid.
//
// This is the UX half. The server-side half (the /paginated + /filter-values
// routes refusing includeAdmin for other roles) ships with the API batch —
// until then a crafted request could still ask for them, so do not treat this
// as the security boundary.
// isAdminRoleName covers the live data's 'Admin' AND 'Administrator' spellings —
// reused rather than re-listed, because the role vocabulary is already split
// across several sources and a second copy would drift.
const isAuditorOrAdmin = (): boolean => {
  const role = currentUserRoleName();
  return isAuditorRoleName(role) || isAdminRoleName(role);
};

// AUD-06: the audit log's "Show Admin Activity" toggle.
export const canSeeAdminActivity = isAuditorOrAdmin;

// AUD-01 (clarified 2026-08-24): the Reviewed / Not-yet-reviewed filter is an
// audit tool, not something an operator needs on the page. Same two roles as
// the admin-activity toggle, named separately so each call site reads for
// itself and the rule still lives in one place.
export const canFilterByReviewState = isAuditorOrAdmin;

// TXN-01 (locked 2026-08-24, reverses b1174): an Administrator may not start,
// confirm, edit or set dwell times. Dwell records work an operator physically
// performed; an admin doing it from a desk breaks segregation of duties.
//
// Admins keep VERIFY — that gate is canMutate(), deliberately not this one —
// under b1175's rule that an admin verification is labelled an override and
// carries a reason.
//
// Auditors are already excluded by canMutate(). The server enforces the same
// rule (middleware/requireRole.denyWrites on /DwellTimers), so this layer is
// UX: it hides controls rather than leaving them to fail on click.
export const canOperateDwell = (): boolean =>
  canMutate() && !isAdminRoleName(currentUserRoleName());
